import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner, useToast } from "../../components/ui";

const SHEET = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info" };
const STATUS = { UNASSIGNED: "To assign", ASSIGNED: "Allocated", IN_PROGRESS: "In progress", DONE: "Done" };
const GROUPS = ["LACN", "CCCMG"];
const PAGE_SIZE = 100;

function fmtDate(iso) {
  if (!iso) return "—";
  try { const d = new Date(iso); return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`; } catch { return "—"; }
}

// Diagnostic view: EVERY record imported in the last N days — including the copies the inventory normally hides
// (duplicates, archived, reworked-out, issues). Admins can see what landed vs what's missing, and force any record
// into production (its group's process queue) temporarily.
export default function AllRecords() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(14);
  const [view, setView] = useState("all");          // all | hidden | visible
  const [q, setQ] = useState("");
  const [sheetF, setSheetF] = useState("");          // "" = all processes
  const [selected, setSelected] = useState(new Set());
  const [group, setGroup] = useState("LACN");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [menu, setMenu] = useState(null);            // { x, y, record }

  const load = useCallback(async () => {
    setRows(null); setErr(null); setSelected(new Set());
    try { const r = await api.allImported(days); setRows(Array.isArray(r) ? r : []); }
    catch (e) { setErr(e.message); setRows([]); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  // close the right-click menu on any outside click
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); };
  }, [menu]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (view === "hidden" && !r.hidden) return false;
      if (view === "visible" && r.hidden) return false;
      if (sheetF && r.sheet !== sheetF) return false;
      if (qq) {
        const hay = `${r.mrn || ""} ${r.orderName || ""} ${r.uniqueId || ""} ${r.clinic || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [rows, view, sheetF, q]);

  useEffect(() => { setPage(0); }, [view, sheetF, q, days]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount - 1);
  const shown = useMemo(() => filtered.slice(pageSafe * PAGE_SIZE, (pageSafe + 1) * PAGE_SIZE), [filtered, pageSafe]);
  const hiddenCount = useMemo(() => (rows || []).filter((r) => r.hidden).length, [rows]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((s) => {
    const ids = shown.map((r) => r.id);
    const allOn = ids.every((id) => s.has(id));
    const n = new Set(s); ids.forEach((id) => (allOn ? n.delete(id) : n.add(id))); return n;
  });

  const moveIds = useCallback(async (ids, g) => {
    if (!ids.length || busy) return;
    setBusy(true); setMenu(null);
    try {
      const res = await api.forceToProduction(ids, g);
      toast(`Moved ${res?.assigned ?? ids.length} record(s) to production → ${g}`, "ok");
      load();
    } catch (e) { toast(e.message, "err"); }
    finally { setBusy(false); }
  }, [busy, toast, load]);

  const onRowContext = (e, r) => {
    e.preventDefault();
    // if the clicked row is part of the current selection, act on the whole selection; else just this row
    setMenu({ x: e.clientX, y: e.clientY, ids: selected.has(r.id) ? [...selected] : [r.id] });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">All imported records</h2>
          <div className="page-sub">Everything imported in the selected window — including hidden duplicates, archived and reworked copies — so you can see what isn’t showing in the inventory, and move any record into production.</div>
        </div>
      </div>

      <div className="filter-bar">
        <input className="inp search" placeholder="Search PID, order, unique ID or clinic…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="inp" value={days} onChange={(e) => setDays(Number(e.target.value))} title="How many days back to show">
          <option value={3}>Last 3 days</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
        </select>
        <select className="inp" value={sheetF} onChange={(e) => setSheetF(e.target.value)} title="Filter by process">
          <option value="">All processes</option>
          {Object.entries(SHEET).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="status-seg">
          {[["all", "All"], ["hidden", "Hidden only"], ["visible", "Visible only"]].map(([k, l]) => (
            <button key={k} className={"seg-btn" + (view === k ? " on" : "")} onClick={() => setView(k)}>{l}</button>
          ))}
        </div>
        <button className="link-btn" onClick={load} disabled={rows === null}>Refresh</button>
        {rows && <span className="hist-count">{filtered.length.toLocaleString()} record{filtered.length === 1 ? "" : "s"}{hiddenCount ? ` · ${hiddenCount.toLocaleString()} hidden` : ""}</span>}
      </div>

      <div className="tbl-wrap">
        {rows === null ? <div className="center-pad"><Spinner /></div>
          : err ? <div className="note note-warn">Couldn’t load: {err}</div>
          : filtered.length === 0 ? <Empty icon={<Icon.clipboard width="40" height="40" />} title="No records" text={view === "hidden" ? "No hidden records in this window — try a longer range, or imports that produced duplicates/reworks." : "Nothing matches — widen the window or clear the filters."} />
          : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 34 }}><input type="checkbox" checked={shown.length > 0 && shown.every((r) => selected.has(r.id))} onChange={togglePage} /></th>
                  <th>PID (MRN)</th>
                  <th>Order</th>
                  <th>Clinic</th>
                  <th>Group</th>
                  <th>Process</th>
                  <th>Status</th>
                  <th>Where it is</th>
                  <th>Imported</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className={r.hidden ? "allrec-hidden" : ""} onContextMenu={(e) => onRowContext(e, r)}>
                    <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                    <td className="mono">{r.mrn || "—"}</td>
                    <td className="wrap">{r.orderName || "—"}</td>
                    <td className="cell-sub">{r.clinic || "—"}</td>
                    <td>{r.group || <span className="cell-sub">—</span>}</td>
                    <td className="cell-sub">{SHEET[r.sheet] || r.sheet || "—"}</td>
                    <td className="cell-sub">{STATUS[r.status] || r.status || "—"}</td>
                    <td><span className={"allrec-badge " + (r.hidden ? "is-hidden" : "is-visible")}>{r.reason}</span></td>
                    <td className="cell-sub">{fmtDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {rows && filtered.length > 0 && (
        <div className="allrec-foot">
          <span className="rev-count" style={{ border: "none", padding: 0, margin: 0 }}>
            {filtered.length.toLocaleString()} record{filtered.length === 1 ? "" : "s"} · page <b>{pageSafe + 1}</b> of <b>{pageCount}</b>
          </span>
          <div className="allrec-pager">
            <button className="btn btn-ghost btn-sm" disabled={pageSafe <= 0} onClick={() => setPage(0)}>« First</button>
            <button className="btn btn-ghost btn-sm" disabled={pageSafe <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</button>
            <button className="btn btn-ghost btn-sm" disabled={pageSafe >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>Next ›</button>
            <button className="btn btn-ghost btn-sm" disabled={pageSafe >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>Last »</button>
          </div>
        </div>
      )}

      {/* Floating action bar — always reachable, no need to scroll back to the top */}
      {selected.size > 0 && (
        <div className="allrec-floatbar">
          <b>{selected.size}</b> selected
          <span style={{ marginLeft: 10 }}>→ production group</span>
          <select className="inp" style={{ height: 32, width: "auto", marginLeft: 6 }} value={group} onChange={(e) => setGroup(e.target.value)}>
            {GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 10 }} onClick={() => moveIds([...selected], group)} disabled={busy}>
            {busy ? "Moving…" : "Move to production"}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* Right-click menu */}
      {menu && (
        <div className="allrec-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <div className="allrec-menu-h">Move {menu.ids.length > 1 ? `${menu.ids.length} records` : "record"} to production</div>
          {GROUPS.map((g) => (
            <button key={g} className="allrec-menu-i" disabled={busy} onClick={() => moveIds(menu.ids, g)}>→ {g}</button>
          ))}
        </div>
      )}
    </div>
  );
}